import React from 'react'

import CardSelect from '../Cards/CardSelect/CardSelect'

import { getLogoBroadcastMedium } from '../../utils/tsUtils'

import { TargetingTypes } from '../../types/targetings.type'
import { BaseComptage } from '../../utils/myAdfactoryApi/swaggerApi'

type BroadcastMediumProps = {
  atLeastOneBaseMsg?: string
  emailingBase?: BaseComptage
  info: string
  targetRequiredText: string
  targetings: TargetingTypes
  onClick: (newData: TargetingTypes) => void
  closeDropdown: () => void
  restrictedMediumTooltipText?: string
}

const isMediumRestricted = (targeting: any, medium: string): boolean =>
  targeting?.restrictedBroadcastMediums?.includes(medium) ?? false

const isMediumVisible = (
  targeting: any,
  medium: string,
  index: number
): boolean => {
  const restricted = isMediumRestricted(targeting, medium)
  const checked = targeting?.checked?.[index] ?? false
  // Hide restricted mediums that aren't carried over from an existing contract;
  // in edit mode they stay visible (checked) so the user can see what was set.
  return !restricted || checked
}

export const handleClickCardSelect = (
  targetings: any,
  market: string,
  index: number,
  onClick: (newData: TargetingTypes) => void
) => {
  const targetingsOriginal = Object.fromEntries(targetings)
  const targeting = targetingsOriginal?.[market]
  const medium = targeting?.broadcastMediums?.[index]

  if (isMediumRestricted(targeting, medium)) return

  const updatedCheckedArray = [...(targeting?.checked ?? [])]

  // If only 1 broadcast for a label, never check/uncheck the card
  if (updatedCheckedArray.length > 1) {
    updatedCheckedArray[index] = !updatedCheckedArray[index]
  }

  const newTargetings = {
    ...targetingsOriginal,
    [market]: {
      ...targeting,
      checked: updatedCheckedArray,
    },
  }

  onClick(newTargetings)
}

export const mapTargetings = (
  targetings: any,
  emailingBase: BaseComptage | undefined,
  atLeastOneBaseMsg: string,
  onClick: (newData: TargetingTypes) => void,
  restrictedMediumTooltipText?: string
) => (
  <>
    {targetings?.map((targeting: any) => {
      const [key, data] = targeting
      const visibleMediums =
        data?.broadcastMediums
          ?.map((medium: string, i: number) => {return { medium, index: i }})
          .filter(({ medium, index }: { medium: string; index: number }) =>
            isMediumVisible(data, medium, index)
          ) ?? []

      const effectiveCheckedCount = visibleMediums.filter(
        ({ medium, index }: { medium: string; index: number }) =>
          data?.checked?.[index] && !isMediumRestricted(data, medium)
      ).length

      const displayEmailingAtLeastOneBaseMsg =
        emailingBase && data?.selected && effectiveCheckedCount === 0

      const forbiddenToUncheck = visibleMediums.length <= 1

      return (
        <div key={key}>
          <div className="title">{data?.label}</div>
          <div className="wrapLogoBroadcastMedium">
            {visibleMediums.map(
              ({ medium, index }: { medium: string; index: number }) => {
                const restricted = isMediumRestricted(data, medium)

                return (
                  <CardSelect
                    data-testid={`imgCardSelect${medium}`}
                    forbiddenToUncheck={forbiddenToUncheck}
                    disabled={restricted}
                    disabledTooltipText={
                      restricted ? restrictedMediumTooltipText : undefined
                    }
                    type="broadcast"
                    key={`cardSelect${medium}`}
                    imgCard={getLogoBroadcastMedium(medium)}
                    isChecked={data?.checked?.[index]}
                    onClickCard={() =>
                      !forbiddenToUncheck &&
                      handleClickCardSelect(targetings, key, index, onClick)
                    }
                    label=""
                  />
                )
              }
            )}
          </div>
          {displayEmailingAtLeastOneBaseMsg && (
            <div className="errorBroadcastMedium">{atLeastOneBaseMsg}</div>
          )}
        </div>
      )
    })}
  </>
)

const BroadcastMedium = ({
  atLeastOneBaseMsg,
  emailingBase,
  info,
  targetRequiredText,
  targetings,
  onClick,
  closeDropdown,
  restrictedMediumTooltipText,
}: BroadcastMediumProps): React.ReactElement => {
  const targetingsArray = (targetings && Object.entries(targetings)) ?? []

  return (
    <div className="wrapperListBroadcastMedium">
      <div
        className="backgroundListBroadcastMedium"
        onClick={closeDropdown}
        aria-hidden="true"
      />
      <div
        data-testid="wrapperBroadcastMediumEmail"
        className="wrapperBroadcastMediumEmail"
      >
        {targetingsArray.length > 0 && info && (
          <span className="infoBroadcastMedium">{info}</span>
        )}
        {targetingsArray.length === 0 && targetRequiredText && (
          <span className="broadcastMediumEmpty">{targetRequiredText}</span>
        )}
        {targetingsArray && (
          <div className="listBroadcastMedium">
            {mapTargetings(
              targetingsArray,
              emailingBase ?? undefined,
              atLeastOneBaseMsg ?? '',
              onClick,
              restrictedMediumTooltipText
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default BroadcastMedium
